import TomSelect from "tom-select";

export function initEmblaProgress(carousel, emblaApi, progress) {
  const progressTrack = progress ?? carousel.querySelector(".progress-track");
  const progressFill = progressTrack?.querySelector(".progress-fill");

  const applyProgress = () => {
    if (!progressTrack || !progressFill) return;
    const total = emblaApi.scrollSnapList().length;
    const canScroll =
      total > 1 && (emblaApi.canScrollPrev() || emblaApi.canScrollNext());
    progressTrack.hidden = !canScroll;
    if (!canScroll) return;

    const selected = emblaApi.selectedScrollSnap();
    const segment = 100 / total;
    progressFill.style.width = `${segment}%`;
    // translate percent is relative to the fill width, so use selected * 100%
    progressFill.style.transform = `translateX(${selected * 100}%)`;
  };

  let raf = 0;
  const onScroll = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      applyProgress();
    });
  };

  emblaApi.on("scroll", onScroll);
  emblaApi.on("reInit", applyProgress);
  emblaApi.on("resize", applyProgress);
  applyProgress();
}

function initSelect() {
  document.querySelectorAll("[data-select]").forEach((el) => {
    new TomSelect(el, {
      maxOptions: false,
      placeholder: el.getAttribute("placeholder") ?? "",
      ...(el.hasAttribute("data-select-search")
        ? {
            plugins: ["dropdown_input"],
          }
        : {
            onInitialize() {
              this.control_input.readOnly = true;
            },
          }),
      render: {
        option: (data, escape) =>
          data.value === ""
            ? ""
            : `<div class="option">${escape(data.text)}${data.icon ? `<svg height="16" width="16" aria-hidden="true"><use href="#${escape(data.icon)}"></use></svg>` : ""}</div>`,
      },
    });
  });
}

/**
 * Fetches Shopify section HTML and patches DOM nodes in-place.
 *
 * @param {Array<string|{source:string,target:string}>} selectors
 *   CSS selectors to update. A string uses the same selector for source and target.
 *   An object lets you read from `source` (in the fetched HTML) and write to `target` (in the current page).
 *
 * @param {string[]} sections  Shopify section IDs to request (mapped 1-to-1 with selectors).
 * @param {string}   [currentUrl]  URL to fetch from (default: current page path).
 * @param {object}   [options]
 * @param {'replace'|undefined} [options.mode]
 *   'replace' swaps the whole element; default patches only innerHTML.
 * @param {boolean}  [options.returnDocument]
 *   When true, returns { data, documents } where documents[i] is the parsed Document for section i.
 *
 * @returns {Promise<object|{data:object,documents:Document[]}>}
 *
 * @example
 * // Simple: same selector on source and target
 * await updateDomComponent(['#cart-count'], ['cart']);
 *
 * // Different source/target selectors
 * await updateDomComponent([{ source: '#cart-items', target: '#cart-drawer-items' }], ['cart']);
 *
 * // Replace the whole element + get back the parsed documents
 * const { data, documents } = await updateDomComponent(
 *   ['#mini-cart'],
 *   ['cart'],
 *   window.location.pathname,
 *   { mode: 'replace', returnDocument: true }
 * );
 */
export async function updateDomComponent(
  selectors,
  sections,
  currentUrl = window.location.pathname,
  options = {},
) {
  if (!selectors?.length || !sections?.length)
    throw new Error("selectors and sections must be non-empty");

  const resolved =
    typeof sections === "string" ? selectors.map(() => sections) : sections;

  if (resolved.length !== selectors.length)
    throw new Error("selectors and sections must be arrays of equal length");

  const url = new URL(currentUrl, window.location.origin);
  url.searchParams.set("sections", [...new Set(resolved)].join(","));

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  const data = await response.json();
  const parser = new DOMParser();
  const documents = options.returnDocument ? [] : null;

  selectors.forEach((entry, index) => {
    const [src, tgt] =
      typeof entry === "string" ? [entry, entry] : [entry.source, entry.target];
    const targetElement = document.querySelector(tgt);
    if (!targetElement || !data[resolved[index]]) return;

    const doc = parser.parseFromString(data[resolved[index]], "text/html");
    const sourceNode = doc.querySelector(src);
    if (!sourceNode) return;

    options.mode === "replace"
      ? targetElement.replaceWith(sourceNode)
      : (targetElement.innerHTML = sourceNode.innerHTML);
    if (documents) documents[index] = doc;
  });

  return options.returnDocument ? { data, documents } : data;
}

export function initEmblaNav(btn, emblaApi, isNext = false) {
  const root = emblaApi.rootNode();
  if (root) {
    if (!root.id) root.id = `embla-${Math.random().toString(36).slice(2, 11)}`;
    btn.setAttribute("aria-controls", root.id);
  }
  if (!btn.getAttribute("aria-label")) {
    btn.setAttribute("aria-label", isNext ? "Slide next" : "Slide prev");
  }

  const update = () => {
    const disabled = !emblaApi[isNext ? "canScrollNext" : "canScrollPrev"]();
    btn.disabled = disabled;
    btn.setAttribute("aria-disabled", disabled ? "true" : "false");
  };

  btn.addEventListener("click", () => {
    if (btn.getAttribute("aria-disabled") === "true") return;
    emblaApi[isNext ? "scrollNext" : "scrollPrev"]();
  });

  ["select", "init", "reInit"].forEach((evt) => emblaApi.on(evt, update));
  update();
}

export const addDotButtonAndClickHandlers = (emblaApi, dotsNode) => {
  if (!dotsNode) return;

  const toggleDotButtonsActive = () => {
    const selected = emblaApi.selectedScrollSnap();
    Array.from(dotsNode.querySelectorAll(".embla__dot")).forEach(
      (dot, index) => {
        dot.classList.toggle("embla__dot--selected", index === selected);
      },
    );
  };

  const addDotBtnsWithClickHandlers = () => {
    const labelTemplate = dotsNode.dataset.labelTemplate || "Go to slide {n}";

    dotsNode.innerHTML = emblaApi
      .scrollSnapList()
      .map((_, index) => {
        const label = labelTemplate.replace("#n", index + 1);
        return `<button class="embla__dot" type="button" aria-label="${label}">
<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
<circle cx="6" cy="6" r="3" stroke="black"/>
</svg>
</button>`;
      })
      .join("");

    Array.from(dotsNode.querySelectorAll(".embla__dot")).forEach(
      (dot, index) => {
        dot.addEventListener("click", () => emblaApi.scrollTo(index), false);
      },
    );
    toggleDotButtonsActive();
  };

  addDotBtnsWithClickHandlers();

  emblaApi
    .on("reInit", addDotBtnsWithClickHandlers)
    .on("select", toggleDotButtonsActive);
};

function initSubmenu() {
  const headerMenu = document.querySelector(".header__menu");
  let closeTimer = null;

  const scheduleClose = () => {
    closeTimer = setTimeout(() => {
      if (
        headerMenu &&
        !headerMenu.querySelector("button.menu__item[aria-expanded='true']")
      ) {
        headerMenu.classList.remove("is-open");
      }
    }, 50);
  };

  const cancelClose = () => {
    clearTimeout(closeTimer);
  };

  document
    .querySelectorAll("button.menu__item[aria-controls]")
    .forEach((trigger) => {
      const submenu = document.getElementById(
        trigger.getAttribute("aria-controls"),
      );
      if (!submenu) return;

      // Funzione per aprire
      const openMenu = () => {
        cancelClose();
        trigger.setAttribute("aria-expanded", "true");
      };

      // Funzione per chiudere
      const closeMenu = () => {
        trigger.setAttribute("aria-expanded", "false");
        scheduleClose();
      };

      // is-open solo dopo che la prima animazione è completata (apertura)
      submenu.addEventListener("transitionend", (e) => {
        if (
          e.propertyName === "opacity" &&
          trigger.getAttribute("aria-expanded") === "true"
        ) {
          headerMenu?.classList.add("is-open");
        }
      });

      // Sincronizza lo stato ARIA all'hover e al focus
      trigger.addEventListener("mouseenter", openMenu);
      trigger.addEventListener("focus", openMenu);
      submenu.addEventListener("mouseenter", openMenu);

      // Chiude allontanando il mouse o perdendo il focus
      trigger.addEventListener("mouseleave", closeMenu);
      submenu.addEventListener("mouseleave", closeMenu);

      // Chiude se il focus si sposta fuori dall'intero gruppo del sottomenu
      const parentLi = trigger.closest("li");
      if (parentLi) {
        parentLi.addEventListener("focusout", (event) => {
          if (!parentLi.contains(event.relatedTarget)) {
            closeMenu();
          }
        });
      }

      // Gestione tasto ESC per chiudere il menu
      document.addEventListener("keydown", (event) => {
        if (
          event.key === "Escape" &&
          trigger.getAttribute("aria-expanded") === "true"
        ) {
          closeMenu();
          trigger.focus(); // Riporta il focus sul trigger principale
        }
      });
    });
}

function initMobMenu() {
  const dialog = document.getElementById("mob-menu");
  if (!dialog) return;

  dialog.addEventListener("click", (e) => {
    // Toggle submenu open
    const btn = e.target.closest(".menu_mob_btn");
    if (btn) {
      const submenu = btn.nextElementSibling;
      if (submenu && submenu.classList.contains("menu_mob_submenu")) {
        e.preventDefault();
        submenu.classList.add("is-open");
        btn.setAttribute("aria-expanded", "true");

        // Focus Management: focus the back button inside the opened submenu
        const backBtn = submenu.querySelector(".menu_mob_back_btn");
        if (backBtn) {
          setTimeout(() => backBtn.focus(), 100);
        }
      }
    }

    // Close submenu (Back button)
    const backBtn = e.target.closest(".menu_mob_back_btn");
    if (backBtn) {
      e.preventDefault();
      const submenu = backBtn.closest(".menu_mob_submenu");
      if (submenu) {
        submenu.classList.remove("is-open");

        // Focus Management: return focus to the button that opened this submenu
        const triggerBtn = dialog.querySelector(
          `[aria-controls="${submenu.id}"]`,
        );
        if (triggerBtn) {
          triggerBtn.setAttribute("aria-expanded", "false");
          triggerBtn.focus();
        }
      }
    }
  });

  // Reset submenus on dialog close to always open fresh
  dialog.addEventListener("close", () => {
    dialog
      .querySelectorAll(".menu_mob_submenu")
      .forEach((sub) => sub.classList.remove("is-open"));
    dialog
      .querySelectorAll(".menu_mob_btn")
      .forEach((btn) => btn.setAttribute("aria-expanded", "false"));
  });
}

function initModals() {
  document.querySelectorAll("dialog.modal").forEach((dialog) => {
    dialog.addEventListener("click", (event) => {
      const rect = dialog.getBoundingClientRect();
      const isInBounds =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!isInBounds) {
        dialog.close();
      }
    });
  });
}

function initHeaderScroll() {
  const mq = window.matchMedia("(min-width: 1280px)");
  const header = document.querySelector(".header__wrapper");
  if (!header) return;

  let lastScroll = 0;

  window.addEventListener("scroll", () => {
    if (!mq.matches) return;

    const currentScroll = window.scrollY;

    if (currentScroll > lastScroll && currentScroll > 100) {
      document.body.classList.add("header-hidden");
    } else {
      document.body.classList.remove("header-hidden");
    }

    lastScroll = currentScroll;
  });
}

function initCollectionLoadMore() {
  document.addEventListener("click", async (e) => {
    const nextLink = e.target.closest("[data-next]");
    const prevLink = e.target.closest("[data-prev]");
    const link = nextLink || prevLink;
    if (!link) return;

    e.preventDefault();
    const url = link.href;
    if (!url) return;

    link.classList.add("loading");
    link.style.pointerEvents = "none";

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newProducts = doc.querySelectorAll(".collection-products > *");
      const container = document.querySelector(".collection-products");

      if (prevLink) {
        const firstProduct = container.firstElementChild;
        const offsetBefore = firstProduct?.offsetTop ?? 0;

        container.prepend(...newProducts);

        const offsetAfter = firstProduct?.offsetTop ?? 0;
        window.scrollBy(0, offsetAfter - offsetBefore);

        const newPrevLink = doc.querySelector("[data-prev]");
        if (newPrevLink) {
          link.href = newPrevLink.href;
          link.classList.remove("loading");
          link.style.pointerEvents = "";
        } else {
          link.closest(".collection-pagination").remove();
        }
      } else {
        newProducts.forEach((node) => container.appendChild(node));
        const newNextLink = doc.querySelector("[data-next]");
        if (newNextLink) {
          link.href = newNextLink.href;
          link.classList.remove("loading");
          link.style.pointerEvents = "";
        } else {
          link.remove();
        }
      }

      // ponytail: update URL for browser back button
      history.pushState(null, "", url);
    } catch {
      link.classList.remove("loading");
      link.style.pointerEvents = "";
    }
  });
}

window.selectFilterTab = function (btn, panelId) {
  const list = btn.closest(".filters-list");
  list
    .querySelectorAll(".tab-btn")
    .forEach((b) => b.classList.remove("active"));
  list
    .querySelectorAll(".tab-content")
    .forEach((p) => p.classList.remove("active"));
  btn.classList.add("active");
  document.getElementById(panelId).classList.add("active");
};

function initTheme() {
  initSelect();
  initSubmenu();
  initMobMenu();
  initModals();
  initHeaderScroll();
  initCollectionLoadMore();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initTheme, { once: true });
} else {
  initTheme();
}
